/**
 * CurrentPlanCard — surface the active subscription plus the four
 * actions V1 owned: Stripe portal, cancel-at-period-end, sync (refresh
 * from Stripe), manage (legacy alias). Centralises the layout that was
 * inlined in V2 ``BillingSettings`` so the section reads as a single
 * card with a clear tier headline, period dates, status chips, and a
 * right-aligned action stack.
 *
 * V1 reference: ``ando-frontend/app/components/billing/CurrentPlanCard.tsx``.
 * V1 owned the i18n hooks; V2 receives Italian copy via the lingui
 * macros at the call site (``BillingSettings.tsx``).
 *
 * Render branches:
 *   - ``billing_mode === "invoice"``     → read-only hint "modifiche tramite
 *                                            account manager".
 *   - ``stripe_enabled === false``       → environment without Stripe; no
 *                                            actions, single sentence.
 *   - ``cancel_at_period_end === true``  → only "Apri portale Stripe" (no
 *                                            second-cancel CTA).
 *   - default                              → primary "Apri portale Stripe"
 *                                            + secondary "Annulla abbonamento".
 *
 * Confirmation dialog for cancel uses the same ``confirm()`` helper V2
 * inherits from ``@ando/ui/dialog`` — the host passes ``onCancel``
 * already wrapped.
 */

import type { BillingSubscriptionResponse } from "@ando/core";
import { Button, cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { CalendarClock, ExternalLink } from "lucide-react";
import * as React from "react";

/**
 * Status-chip tone derived from the BE ``status`` string. We tolerate
 * Stripe's full enum (``trialing`` / ``active`` / ``past_due`` /
 * ``canceled`` / ``incomplete`` / …) — defaults to a neutral tone when
 * the BE ships a value we don't yet enumerate so the chip never blocks
 * the page render on a schema bump.
 */
function statusTone(
  status: string,
): { label: ReturnType<typeof msg> | string; cls: string } {
  switch (status) {
    case "active":
      return { label: msg`Attivo`, cls: "bg-success/15 text-success" };
    case "trialing":
      return { label: msg`In prova`, cls: "bg-accent/15 text-accent" };
    case "past_due":
      return { label: msg`Pagamento in ritardo`, cls: "bg-warning/15 text-warning" };
    case "canceled":
      return { label: msg`Annullato`, cls: "bg-error/15 text-error" };
    case "incomplete":
    case "incomplete_expired":
      return { label: msg`Da completare`, cls: "bg-warning/15 text-warning" };
    case "unpaid":
      return { label: msg`Non pagato`, cls: "bg-error/15 text-error" };
    default:
      return { label: status, cls: "bg-ink-3/10 text-ink-3" };
  }
}

export interface CurrentPlanCardProps {
  readonly subscription: BillingSubscriptionResponse;
  readonly onOpenPortal?: () => void;
  readonly onCancel?: () => void;
  readonly portalPending?: boolean;
  readonly cancelPending?: boolean;
  readonly className?: string;
}

export const CurrentPlanCard: React.FC<CurrentPlanCardProps> = ({
  subscription,
  onOpenPortal,
  onCancel,
  portalPending = false,
  cancelPending = false,
  className,
}) => {
  const { t, i18n } = useLingui();
  const tone = statusTone(subscription.status);
  const toneLabel =
    typeof tone.label === "string" ? tone.label : i18n._(tone.label);

  const periodLabel = React.useMemo(() => {
    if (!subscription.current_period_end) return null;
    const end = new Date(subscription.current_period_end).toLocaleDateString(
      "it-IT",
      { day: "2-digit", month: "long", year: "numeric" },
    );
    if (subscription.cancel_at_period_end) {
      return t`Termine: ${end}`;
    }
    return t`Rinnovo: ${end}`;
  }, [subscription.current_period_end, subscription.cancel_at_period_end, t]);

  const isFreeTier = subscription.tier.toLowerCase() === "free";

  return (
    <article
      className={cn(
        "flex flex-col gap-4 rounded-lg border border-hairline bg-card/55 p-4",
        "sm:flex-row sm:items-start sm:justify-between",
        className,
      )}
    >
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="font-display font-semibold text-[22px] text-ink-1 tracking-tight">
            {subscription.tier.toUpperCase()}
          </span>
          <span
            className={cn(
              "inline-flex items-center rounded-full px-1.5 py-0.5 font-medium text-[10.5px]",
              tone.cls,
            )}
          >
            {toneLabel}
          </span>
          {subscription.is_trialing && subscription.status !== "trialing" ? (
            <span className="inline-flex items-center rounded-full bg-accent/15 px-1.5 py-0.5 font-medium text-[10.5px] text-accent">
              trial
            </span>
          ) : null}
        </div>
        {periodLabel ? (
          <div className="flex items-center gap-1.5 text-[12px] text-ink-3">
            <CalendarClock className="h-3 w-3" aria-hidden strokeWidth={2} />
            <span>{periodLabel}</span>
          </div>
        ) : null}
        {subscription.cancel_at_period_end ? (
          <p className="text-[11.5px] text-warning">
            <Trans>L'abbonamento NON sarà rinnovato alla scadenza.</Trans>
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col items-stretch gap-1.5 sm:items-end">
        {subscription.billing_mode === "invoice" ? (
          <p className="max-w-[240px] text-[11.5px] text-ink-3 sm:text-right">
            <Trans>Piano a fattura — modifiche tramite il tuo account manager.</Trans>
          </p>
        ) : !subscription.stripe_enabled ? (
          <p className="text-[11.5px] text-ink-3">
            <Trans>Fatturazione non abilitata su questo ambiente.</Trans>
          </p>
        ) : (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={onOpenPortal}
              disabled={portalPending}
            >
              <ExternalLink className="h-3 w-3" strokeWidth={2} aria-hidden />
              {portalPending ? t`Apertura…` : t`Apri portale Stripe`}
            </Button>
            {!subscription.cancel_at_period_end && !isFreeTier && onCancel ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                disabled={cancelPending}
                className="text-error hover:bg-error/10"
              >
                {cancelPending ? t`Annullamento…` : t`Annulla abbonamento`}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </article>
  );
};
CurrentPlanCard.displayName = "CurrentPlanCard";
